import { FC, useState } from 'react';
import { FlightReport, IFlightReport } from '../../../../model/IFlightReport';
import FlightReportStatus from '../../../../model/enum/FlightReportStatus';
import FlightReportFlagged from '../../FlightReportFlagged';
import { downloadPdf, showPdf } from '../../../../services/PdfService';
import { GoAButton, GoAButtonGroup, GoACircularProgress, GoAModal } from '@abgov/react-components';
import { useAuth } from 'react-oidc-context';
import { AuthenticationService } from '../../../../services/authentication/AuthenticationService';
import CalloutVariant from '../../../../model/enum/CalloutVariant';
import { publishToast } from '../../../../components/Toast/toast';
import { useNavigate } from 'react-router-dom';
import { getCorporateRegion } from '../../../../services/domainServices/CorporateRegionDomainService';
import { FlightReportCRUD } from '../../../../operations/FlightReportCRUD';
import axios from 'axios';
import { IValidationError } from '../../../../model/errors/IValidationError';
import { ICorporateRegion } from '../../../../model/ICorporateRegion';
import { IPaginationResult } from '../../../../model/search/IPaginationResult';
import { IValidation } from '../../../../model/errors/IValidation';
import styles from './ActionBar.module.scss';
const { container, spacer } = styles;
interface IActionBarProps {
  flightReport: IFlightReport;
  corporateRegionResult: IPaginationResult<ICorporateRegion>;
  canSignOff: boolean;
  onChange: (updatedFields: Partial<IFlightReport>) => void;
}
const ActionBar: FC<IActionBarProps> = ({ flightReport, onChange, corporateRegionResult, canSignOff }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const downloadClick = async () => {
    setIsLoading(true);

    await downloadPdf(
      auth.user?.access_token as string,
      flightReport.flightReportId,
      flightReport.contractRegistration?.aircraftRegistration as string,
    );

    setIsLoading(false);
  };

  const [showModal, setShowModal] = useState(false);
  const [headerModal, setHeaderModal] = useState('Are you sure you want to sign off on this report?');
  const [cancelButton, setCancelButton] = useState('No, cancel');
  const [submitButton, setSubmitButton] = useState('Yes, I do');

  const [returnedButton, setReturnedButton] = useState(false);
  const [approvedButton, setApprovedButton] = useState(false);
  const [deleteButton, setDeleteButton] = useState(false);
  const [unsignButton, setUnsignButton] = useState(false);
  const [dispatcherReturnedButton, setDispatcherReturnedButton] = useState(false);

  const [notificationMessage, setNotificationMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [showNotificationMessage, setShowNotificationMessage] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [calloutVariant, setCalloutVariant] = useState(CalloutVariant.success);

  //Email Notification to WFOPs email when AA returns a flight report
  const [showWFOPsNotificationMessage, setShowWFOPsNotificationMessage] = useState(false);
  const [WFOPsNotificationMessage, setWFOPsNotificationMessage] = useState('');

  const [modalHeading] = useState('');

  async function onSignedOff() {
    setDispatcherReturnedButton(false);
    setReturnedButton(false);
    setApprovedButton(false);
    setDeleteButton(false);
    setUnsignButton(false);
    setWarningMessage('');
    setHeaderModal('Are you sure you want to sign off on this report?');
    setCancelButton('No, cancel');
    setSubmitButton('Yes, I do');
    setShowModal(true);
  }

  async function onUnSignOff() {
    setUnsignButton(true);
    setDispatcherReturnedButton(false);
    setReturnedButton(false);
    setApprovedButton(false);
    setDeleteButton(false);
    setHeaderModal('Are you sure you want to unsign off on this report?');
    setCancelButton('No, cancel');
    setSubmitButton('Yes, I do');
    setShowModal(true);
  }

  async function onReturned() {
    setReturnedButton(true);
    setDispatcherReturnedButton(false);
    setApprovedButton(false);
    setDeleteButton(false);
    setUnsignButton(false);
    setHeaderModal('Are you sure you want to return on this report?');
    setCancelButton('Cancel');
    setSubmitButton('Yes, Return');
    setShowModal(true);
  }

  async function onDispatcherReturned() {
    setDispatcherReturnedButton(true);
    setReturnedButton(false);
    setApprovedButton(false);
    setDeleteButton(false);
    setUnsignButton(false);
    setHeaderModal('Are you sure you want to return on this report?');
    setCancelButton('Cancel');
    setSubmitButton('Yes, Return');
    setShowModal(true);
  }

  async function onApproved() {
    setApprovedButton(true);
    setDispatcherReturnedButton(false);
    setReturnedButton(false);
    setDeleteButton(false);
    setUnsignButton(false);
    setHeaderModal('Are you sure you want to approve on this report?');
    setCancelButton('Cancel');
    setSubmitButton('Yes, Approve');
    setShowModal(true);
  }

  async function onCancel() {
    setIsLoading(true);
    setShowModal(false);
    setDispatcherReturnedButton(false);
    setApprovedButton(false);
    setReturnedButton(false);
    setDeleteButton(false);
    setUnsignButton(false);

    setIsLoading(false);
  }

  async function onDelete() {
    setDeleteButton(true);
    setDispatcherReturnedButton(false);
    setApprovedButton(false);
    setReturnedButton(false);
    setUnsignButton(false);

    setHeaderModal(
      'You are about to delete this flight report, flying hours, expenses, notes and unserviceable information. Do you want to proceed?',
    );
    setCancelButton('No, cancel');
    setSubmitButton('Yes, I do');
    setShowModal(true);
  }

  //On signed off yes submit button
  async function onSubmit(status: string = FlightReportStatus.signedOff, ignoreWarning: boolean = true) {
    setIsLoading(true);

    let newValue = new FlightReport();
    if (status !== FlightReportStatus.inProgress)
      newValue = new FlightReport({
        ...flightReport,
        isFlagged: false,
        status: status,
      });
    else newValue = new FlightReport({ ...flightReport, status: status });

    //Get corporate region if it get undefined
    if (newValue.corporateRegion === undefined && newValue.corporateRegionId) {
      var corporateRegion = getCorporateRegion(corporateRegionResult, newValue.corporateRegionId);

      newValue.corporateRegion = corporateRegion;
    }

    try {
      //Update flag status
      const response = await FlightReportCRUD.createAndUpdate(newValue, auth, auth.user?.access_token as string, ignoreWarning);
      // .then((response: any) => {
      if (response.data) {
        const updatedFlightReport = new FlightReport({
          ...newValue,
          hasPdf: response.data.hasPdf,
          hasWFOPsEmailSuccess: response.data.hasWFOPsEmailSuccess,
        });

        //Email Notification to WFOPs email when AA returns a flight report
        if (updatedFlightReport.status === FlightReportStatus.returned && !updatedFlightReport.hasWFOPsEmailSuccess) {
          setShowWFOPsNotificationMessage(true);
          setWFOPsNotificationMessage(
            'Flight report ' +
              updatedFlightReport.flightReportId +
              ' was returned, but the notification email to Wildfire Operations failed. Please notify them manually.',
          );
        } else if (updatedFlightReport.status === FlightReportStatus.needsAttention) {
          navigate('/dashboard');
          publishToast({
            type: 'success',
            message: 'Flight report ' + updatedFlightReport.flightReportId + ' has been returned.',
          });
        }

        //pass update value to parent to FlightReportDetail
        onChange(updatedFlightReport);
        setWarning(response.validationResult ?? []);
      }
      // })
      // .catch((error: any) => {
      //     setError(error);
      // });

      setShowModal(false);

      setIsLoading(false);
    } catch (error: any) {
      setError(error);
      console.error(error);
      setShowModal(false);
      setIsLoading(false);
    }
  }

  //On click delete button
  async function onSubmitDelete() {
    setIsLoading(true);

    let newValue = new FlightReport();
    newValue = new FlightReport({ ...flightReport });

    try {
      await FlightReportCRUD.deleteFlightReportById(newValue, auth, auth.user?.access_token as string)
        .then((response: any) => {
          navigate('/dashboard');
        })
        .catch((error) => {
          setError(error);
        });
      setShowModal(false);
      setIsLoading(false);
    } catch (e: any) {
      console.log(e);
      setShowModal(false);
      setIsLoading(false);
    }
  }

  async function setError(error: any) {
    var validationError = '';

    if (axios.isAxiosError(error)) {
      if (error.response) {
        var validation = error.response.data as IValidationError;
        if (validation) {
          validation.validationResult?.forEach((record: any, index: any) => {
            if (record && record.type === 'Error') validationError += '<li>' + record.message + '</li>';
          });
        }

        if (validation?.errorMessage) {
          validationError = validationError + '<p>' + validation.errorMessage + '</p>';
        }

        if (validationError !== '') {
          setCalloutVariant(CalloutVariant.emergency);
          setNotificationMessage(validationError);
          setShowNotificationMessage(true);
          setIsLoading(false);
        } else setWarning(error.response.data.validationResult ?? []);
      }
    } else {
      console.error('else', error);
    }
  }

  async function setWarning(validationResult: IValidation[]) {
    var validationWarning = '';

    if (validationResult) {
      validationResult.forEach((record: any, index: any) => {
        if (record && record.type === 'Warning') validationWarning += '<li>' + record.message + '</li>';
      });
      if (validationWarning !== '') setShowWarningMessage(true);
      else setShowWarningMessage(false);

      setWarningMessage(validationWarning);
    }
  }
  return (
    <>
      <GoACircularProgress variant='fullscreen' size='small' visible={isLoading} />
      <div className={container}>
        <div>
          <div>
            <FlightReportFlagged flightReport={flightReport} onChange={onChange} />
          </div>
          {showPdf(flightReport) && (
            <div>
              <GoAButton size='compact' type='tertiary' onClick={downloadClick} trailingIcon='document-text'></GoAButton>
            </div>
          )}
        </div>
        <div className={spacer} />
        <div>
          {(flightReport?.status === FlightReportStatus.submitted ||
            flightReport?.status === FlightReportStatus.inProgress ||
            flightReport?.status === FlightReportStatus.returned) && (
            <div>
              {auth.isAuthenticated &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_D', auth.user?.access_token as string) && (
                  <div>
                    <GoAButton size='compact' type='secondary' onClick={() => onDelete()}>
                      Delete
                    </GoAButton>
                  </div>
                )}
            </div>
          )}

          {/* [Dispatcher] Return flight report to pilot */}
          {flightReport?.pilotEmailId !== '' &&
            (flightReport?.status === FlightReportStatus.submitted ||
              flightReport?.status === FlightReportStatus.inProgress ||
              flightReport?.status === FlightReportStatus.returned) && (
              <div>
                {auth.isAuthenticated &&
                  AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
                  AuthenticationService.hasResourceRole('FlightReports', 'Avi_FlightRptDisps_W', auth.user?.access_token as string) && (
                    <div>
                      <GoAButton testId='btnDispatcherReturn' size='compact' type='secondary' onClick={() => onDispatcherReturned()}>
                        Return
                      </GoAButton>
                    </div>
                  )}
              </div>
            )}

          {(flightReport?.status === FlightReportStatus.inProgress || flightReport?.status === FlightReportStatus.returned) && (
            <div>
              {auth.isAuthenticated &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRptSignoff_E', auth.user?.access_token as string) && (
                  <div>
                    <GoAButton size='compact' type='primary' disabled={!canSignOff} onClick={() => onSignedOff()}>
                      Sign off
                    </GoAButton>
                  </div>
                )}
            </div>
          )}

          {flightReport?.status === FlightReportStatus.signedOff && (
            <div>
              {auth.isAuthenticated &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRptSignoff_E', auth.user?.access_token as string) && (
                  <div>
                    <GoAButton size='compact' type='primary' onClick={() => onUnSignOff()}>
                      Unsign off
                    </GoAButton>
                  </div>
                )}
            </div>
          )}

          {/* [AircraftAccount] Return flight report */}
          {(flightReport?.status === FlightReportStatus.signedOff || flightReport?.status === FlightReportStatus.approved) && (
            <div>
              {auth.isAuthenticated &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRptAppRet_E', auth.user?.access_token as string) && (
                  <div>
                    <GoAButton size='compact' type='primary' onClick={() => onReturned()}>
                      Return
                    </GoAButton>
                  </div>
                )}
            </div>
          )}

          {flightReport?.status === FlightReportStatus.signedOff && (
            <div>
              {auth.isAuthenticated &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
                AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRptAppRet_E', auth.user?.access_token as string) && (
                  <div>
                    <GoAButton size='compact' type='primary' onClick={() => onApproved()}>
                      Approve
                    </GoAButton>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
      <GoAModal
        heading=''
        open={showModal}
        actions={
          <GoAButtonGroup alignment='end'>
            <GoAButton size='compact' type='secondary' onClick={() => onCancel()}>
              {cancelButton}
            </GoAButton>
            <GoAButton
              size='compact'
              type='primary'
              onClick={() =>
                returnedButton
                  ? onSubmit(FlightReportStatus.returned)
                  : dispatcherReturnedButton
                    ? onSubmit(FlightReportStatus.needsAttention)
                    : approvedButton
                      ? onSubmit(FlightReportStatus.approved)
                      : deleteButton
                        ? onSubmitDelete()
                        : unsignButton
                          ? onSubmit(FlightReportStatus.inProgress)
                          : onSubmit(FlightReportStatus.signedOff, false)
              }
            >
              {submitButton}
            </GoAButton>
          </GoAButtonGroup>
        }
      >
        <p>{headerModal}</p>
      </GoAModal>

      {flightReport?.status !== FlightReportStatus.inProgress && flightReport?.status !== FlightReportStatus.returned && (
        <div>
          <GoAModal
            heading={modalHeading}
            calloutVariant={calloutVariant}
            open={showNotificationMessage}
            actions={
              <GoAButtonGroup alignment='end'>
                <GoAButton onClick={() => setShowNotificationMessage(false)}>Okay</GoAButton>
              </GoAButtonGroup>
            }
          >
            <div
              dangerouslySetInnerHTML={{
                __html: notificationMessage,
              }}
            />
          </GoAModal>
        </div>
      )}
      {(flightReport?.status === FlightReportStatus.inProgress || flightReport?.status === FlightReportStatus.returned) && (
        <div>
          <GoAModal
            heading='This flight report cannot be signed off'
            open={showNotificationMessage}
            onClose={() => setShowNotificationMessage(false)}
            actions={<GoAButtonGroup alignment='end'></GoAButtonGroup>}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: notificationMessage,
              }}
            />
          </GoAModal>
        </div>
      )}
      {(flightReport?.status === FlightReportStatus.inProgress || flightReport?.status === FlightReportStatus.returned) &&
        (showNotificationMessage === undefined || showNotificationMessage === false) &&
        returnedButton === false &&
        deleteButton === false &&
        unsignButton === false &&
        approvedButton === false &&
        dispatcherReturnedButton === false &&
        warningMessage !== undefined &&
        warningMessage !== '' && (
          <div>
            <GoAModal
              heading='This flight report cannot be signed off'
              open={showWarningMessage}
              onClose={() => setShowWarningMessage(false)}
              actions={
                <GoAButtonGroup alignment='end'>
                  <GoAButton onClick={() => setShowWarningMessage(false)}>No, cancel</GoAButton>
                  <GoAButton onClick={() => onSubmit(FlightReportStatus.signedOff, true)}>Yes, continue</GoAButton>
                </GoAButtonGroup>
              }
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: warningMessage,
                }}
              />
            </GoAModal>
          </div>
        )}

      {/* Email Notification to WFOPs email when AA returns a flight report */}
      {flightReport?.status === FlightReportStatus.returned && !flightReport?.hasWFOPsEmailSuccess && (
        <div>
          <GoAModal open={showWFOPsNotificationMessage}>
            <p>{WFOPsNotificationMessage}</p>
            <GoAButtonGroup alignment='end' mt='xl'>
              <GoAButton
                onClick={() => {
                  setShowWFOPsNotificationMessage(false);
                  setWFOPsNotificationMessage('');
                }}
              >
                Okay
              </GoAButton>
            </GoAButtonGroup>
          </GoAModal>
        </div>
      )}
    </>
  );
};

export default ActionBar;
